import { Select, FlagIcon, SelectValue } from "@oysterhr/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import type { Country, CountryGroup, CountrySelectProps } from "./types"

export function CountryOption({ country, ...rest }: { country: Country }) {
  return (
    <Select.Option
      startAdornment={<FlagIcon countryCode={country.code} />}
      value={country.code}
      {...rest}
    >
      {country.name}
    </Select.Option>
  )
}
CountryOption.displayName = "Select.Option"

export function SelectedCountryOption({ country }: { country: Country }) {
  // TODO: Update this to use Select.Option. At the moment, using Select.Option is not possible because its classNames are not made for a selected option.
  // See PR https://github.com/oysterhr/anywhere/pull/8935 for more info
  return (
    <div className="grid w-full cursor-pointer grid-cols-[max-content,1fr,max-content] items-center gap-2">
      <FlagIcon countryCode={country.code} />
      <span className="overflow-hidden text-ellipsis whitespace-nowrap">{country.name}</span>
    </div>
  )
}

function isACountryGroup(countries: Country[] | CountryGroup[]): countries is CountryGroup[] {
  return countries[0] && Object.prototype.hasOwnProperty.call(countries[0], "options")
}

function renderCountryGroup(group: CountryGroup) {
  return (
    <Select.Group key={group.label} label={group.label}>
      {group.options.map(country => (
        <CountryOption country={country} key={country.code} />
      ))}
    </Select.Group>
  )
}

export function CountrySelect({ countries, searchable, label, ...rest }: CountrySelectProps) {
  const [searchText, setSearchText] = useState("")
  const { t } = useTranslation("Common")

  const changeHandler = (value: SelectValue | undefined) => {
    if (rest?.onChange) {
      rest.onChange(value)
    }
    setSearchText("")
  }

  const blurHandler = () => {
    if (rest?.onBlur) {
      rest.onBlur()
    }
    setSearchText("")
  }

  const filteredCountries = useMemo(() => {
    if (!searchText) return countries

    if (isACountryGroup(countries)) {
      return countries
        .map(group => ({
          ...group,
          options: group.options.filter(country =>
            country.name.toLowerCase().includes(searchText.toLowerCase()),
          ),
        }))
        .filter(group => group.options.length > 0)
    } else {
      return countries.filter(country =>
        country.name.toLowerCase().includes(searchText.toLowerCase()),
      )
    }
  }, [searchText, countries])

  return (
    <Select
      label={label}
      UNSAFE_selectedValueDisplay={value => {
        if (!value) {
          return null
        }

        const selectedCountry = isACountryGroup(countries)
          ? countries
              .map(({ options }) => options)
              .flat()
              .find(country => country.code === value)
          : countries.find(country => country.code === value)

        if (!selectedCountry) {
          return null
        }

        return <SelectedCountryOption country={selectedCountry} />
      }}
      {...rest}
      onBlur={blurHandler}
      onChange={changeHandler}
    >
      {searchable && (
        <Select.Input
          placeholder={t("search")}
          onChange={e => setSearchText(e.target.value || "")}
          value={searchText}
        />
      )}
      {isACountryGroup(filteredCountries)
        ? filteredCountries.map(renderCountryGroup)
        : filteredCountries.map(country => <CountryOption country={country} key={country.code} />)}
    </Select>
  )
}
